import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { exportPdfFromRow } from "../core/utils/exportPdfFromRow";
import { formatCharacterizationDate } from "../core/utils/utils";
import { exportCsvFromRow } from "../core/utils/exportCsvFromRow";
import { exportEmailFromRow } from "../core/utils/exportEmailFromRow";
import {
  getRegulationChipLabel,
  getRegulationTypeFromContainer,
} from "../characterizationDetails/constants/regulation";
import PdfExportProgressOverlay from "../../../components/loader/PdfExportProgressOverlay";
import PdfModal from "../../../components/PdfModal";
import exporter from "../../../static/assets/Exporter.svg";
import { getAppZoom } from "../../settings/client/clientUtils";

const STATUS_VARIANTS = {
  Validé: {
    icon: "fas fa-check",
    badgeClass: "bg-[#E9FBFB] text-[#2AB9B8]",
  },
  Validated: {
    icon: "fas fa-check",
    badgeClass: "bg-[#E9FBFB] text-[#2AB9B8]",
  },
  ImageEnTraitement: {
    icon: "fas fa-image",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  "En traitement": {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  "In progress": {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  InProgress: {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  "En cours": {
    icon: "fas fa-history",
    badgeClass: "bg-[#EFEFFF] text-[#5F5BFF]",
  },
  ACompleter: {
    icon: "fas fa-pen",
    badgeClass: "bg-[#E6ECFD] text-[#0136BD]",
  },
  "A compléter": {
    icon: "fas fa-pen",
    badgeClass: "bg-[#E6ECFD] text-[#0136BD]",
  },
  "À compléter": {
    icon: "fas fa-pen",
    badgeClass: "bg-[#E6ECFD] text-[#0136BD]",
  },
  CorrectionEnTraitement: {
    icon: "fas fa-tasks",
    badgeClass: "bg-[#FFF4E6] text-[#B8860B]",
  },
  Erreur: {
    icon: "fas fa-exclamation-triangle",
    badgeClass: "bg-[#FBEBED] text-[#AF2F3C]",
    helperIcon: "fas fa-question-circle",
  },
  Error: {
    icon: "fas fa-exclamation-triangle",
    badgeClass: "bg-[#FBEBED] text-[#AF2F3C]",
    helperIcon: "fas fa-question-circle",
  },
};

const StatusBadge = ({ status }) => {
  const { t } = useTranslation();
  const variant = STATUS_VARIANTS[status] ?? {
    icon: "fas fa-question-circle",
    badgeClass: "char-badge-outline",
  };
  const badgeClassMap = {
    "bg-[#E9FBFB] text-[#2AB9B8]": "char-badge-validated",
    "bg-[#EFEFFF] text-[#5F5BFF]": "char-badge-progress",
    "bg-[#FBEBED] text-[#AF2F3C]": "char-badge-error",
    "bg-[#E6ECFD] text-[#0136BD]": "char-badge-tocomplete",
    "bg-[#FFF4E6] text-[#B8860B]": "char-badge-correction",
    "bg-gray-100 text-gray-600": "char-badge-outline",
  };
  const badgeClass = badgeClassMap[variant.badgeClass] || variant.badgeClass;
  const statusLabel =
    status === "ImageEnTraitement"
      ? t("Image en traitement")
      : status === "ACompleter"
        ? t("A compléter")
        : status === "CorrectionEnTraitement"
          ? t("Correction en traitement")
          : t(status);
  return (
    <span className={`char-badge ${badgeClass}`}>
      <i className={`${variant.icon} text-xs text-current`} />
      {statusLabel}
      {variant.helperIcon && (
        <i className={`${variant.helperIcon} text-xs char-text-secondary`} />
      )}
    </span>
  );
};

const CharacterizationRow = ({
  period,
  container,
  superContainer,
  containers,
  zone,
  fromAllReports = false,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({
    percent: 0,
    label: "",
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState([]);
  const [openModalExport, setOpenModalExport] = useState(false);
  const [initialPdfModalAction, setInitialPdfModalAction] = useState("export");
  const exportButtonRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const client = period?.clientParsed ?? { name: "-", address: "-" };
  const clientName = client.name ? client.name.toUpperCase() : "-";
  const clientAddress = client.address
    ? client.address.charAt(0).toUpperCase() +
      client.address.slice(1).toLowerCase()
    : "-";
  const wasteName = period?.wasteCategory?.wasteCategoryName ?? null;
  const regulationLabel = getRegulationChipLabel(
    getRegulationTypeFromContainer(container),
    t,
  );
  const weightNum = period?.weight != null ? Number(period.weight) : NaN;

  const canExport =
    Boolean(
      client?.name &&
        String(client.name).trim() !== "-" &&
        String(client.name).trim() !== "",
    ) &&
    !Number.isNaN(weightNum) &&
    weightNum > 0 &&
    Boolean(
      wasteName &&
        String(wasteName).trim() !== "-" &&
        String(wasteName).trim() !== "",
    );
  const startLabel = period.startDate
    ? formatCharacterizationDate(period.startDate)
    : "-";
  const lastUpdateLabel = period.lastUpdateDate
    ? formatCharacterizationDate(period.lastUpdateDate)
    : "-";

  const updateMenuPosition = useCallback(() => {
    if (!exportButtonRef.current) return;
    const rect = exportButtonRef.current.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom + 4,
      left: rect.right,
    });
  }, []);

  useEffect(() => {
    if (!isDropdownOpen) return;
    updateMenuPosition();
    const onScrollOrResize = () => updateMenuPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isDropdownOpen, updateMenuPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        exportButtonRef.current?.contains(event.target) ||
        event.target.closest(".char-dropdown-export-portal")
      ) {
        return;
      }
      setIsDropdownOpen(false);
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleExportPdf = async (event) => {
    if (!canExport) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    setExportProgress({ percent: 0, label: t("Loading...") });
    try {
      await exportPdfFromRow({
        period,
        container,
        superContainer,
        client,
        i18n,
        t,
        onProgress: ({ percent, label }) =>
          setExportProgress({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting PDF:", error);
    } finally {
      setIsExporting(false);
      setExportProgress({ percent: 0, label: "" });
    }
  };

  const handleExportCsv = async (event) => {
    if (!canExport) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    setExportProgress({ percent: 5, label: t("Loading...") });
    try {
      await exportCsvFromRow({
        period,
        container,
        superContainer,
        i18n,
        t,
        onProgress: ({ percent, label }) =>
          setExportProgress({
            percent,
            label: label || t("Loading..."),
          }),
      });
    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setIsExporting(false);
      setExportProgress({ percent: 0, label: "" });
    }
  };

  const handleExportEmail = async (event) => {
    if (!canExport) return;
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsDropdownOpen(false);
    setIsExporting(true);
    try {
      await exportEmailFromRow({
        period,
        container,
        superContainer,
        client,
        i18n,
        t,
        setPdfFile,
        setOpenModalExport,
        setInitialPdfModalAction,
        selectedFilesForExport: [],
      });
    } catch (error) {
      console.error("Error exporting email:", error);
    } finally {
      setIsExporting(false);
    }
  };
  return (
    <div
      className="table-standard-row"
      onClick={() => {
        history.push(`/characterization/details/${container._id}`, {
          period,
          client,
          container,
          superContainer,
          containers,
          zone,
          listSearch: location.search || "",
          fromAllReports,
        });
      }}
    >
      <div className="table-standard-row-cell" data-label={t("Name")}>
        <span className="table-standard-row-cell-truncate">{startLabel}</span>
      </div>
      <div className="table-standard-row-cell" data-label={t("Status")}>
        <StatusBadge status={period.status} />
      </div>
      <div className="table-standard-row-cell" data-label={t("Waste origin")}>
        <div className="char-origin-cell-content flex items-center gap-2 min-w-0">
          <div className="char-client-display-avatar shrink-0">
            <span className="char-client-display-avatar-text">
              {client.name?.slice(0, 2)?.toUpperCase() || "-"}
            </span>
          </div>
          <div className="char-origin-cell-lines">
            <span className="char-origin-cell-truncate">{clientName}</span>
            <span className="char-origin-cell-truncate text-gray-500 text-sm">
              {clientAddress}
            </span>
          </div>
          {period.clientSource === "legacy" && (
            <span
              className="shrink-0 cursor-help text-gray-500"
              title={t(
                "Legacy waste origin: data not linked to a unique record",
              )}
              aria-label={t(
                "Legacy waste origin: data not linked to a unique record",
              )}
            >
              <i className="fa fa-info-circle text-sm" aria-hidden />
            </span>
          )}
        </div>
      </div>
      <div
        className="table-standard-row-cell"
        data-label={t("Last modification")}
      >
        <span className="char-text-14 char-text-primary char-font-normal">
          {lastUpdateLabel}
        </span>
      </div>
      <div
        className="table-standard-row-cell table-standard-row-actions"
        data-label=""
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={exportButtonRef}
          type="button"
          className="char-button char-button--table-action"
          onClick={(e) => {
            e.stopPropagation();
            if (!canExport) return;
            if (!isDropdownOpen) {
              updateMenuPosition();
            }
            setIsDropdownOpen(!isDropdownOpen);
          }}
          disabled={isExporting || !canExport}
          aria-expanded={isDropdownOpen}
        >
          <img src={exporter} alt="export" className="char-button-icon" />
          {t("Export")}
        </button>
        {isDropdownOpen &&
          createPortal(
            <div
              className="char-dropdown char-dropdown--compact char-dropdown-export-portal"
              style={{
                position: "fixed",
                top: menuPosition.top,
                left: menuPosition.left,
                right: "auto",
                marginTop: 3,
                zIndex: 9999,
                transform: `translateX(-100%) scale(${getAppZoom()})`,
                transformOrigin: "top right",
                width: "auto",
                minWidth: 168,
                maxWidth: 220,
              }}
            >
              <button
                type="button"
                onClick={handleExportPdf}
                className="char-dropdown-item"
              >
                <img
                  src={exporter}
                  alt="export"
                  className="char-dropdown-item-icon"
                />
                <span>{`${t("PDF")} — ${regulationLabel}`}</span>
              </button>
              <button
                type="button"
                onClick={handleExportCsv}
                className="char-dropdown-item"
              >
                <img
                  src={exporter}
                  alt="export"
                  className="char-dropdown-item-icon"
                />
                <span>{`${t("CSV")} — ${regulationLabel}`}</span>
              </button>
              <div className="char-dropdown-item-divider" />
              <button
                type="button"
                onClick={handleExportEmail}
                className="char-dropdown-item"
              >
                <i className="fas fa-envelope char-text-secondary" />
                <span>{t("Send by email")}</span>
              </button>
            </div>,
            document.body,
          )}
      </div>
      {isExporting && (
        <PdfExportProgressOverlay
          percent={exportProgress.percent}
          label={exportProgress.label}
        />
      )}
      {openModalExport && (
        <PdfModal
          data={{
            ...container,
            wasteName: period.wasteCategory
              ? period.wasteCategory.wasteCategoryName
              : "-",
            positioning: container?.positioning || "",
          }}
          setOpenModalExport={setOpenModalExport}
          openModalExport={openModalExport}
          setLoadScreen={setIsExporting}
          setPdfFile={setPdfFile}
          pdfFile={pdfFile}
          characterization={true}
          pdfDateTime={new Date(period?.start || period?._time || new Date())}
          initialAction={initialPdfModalAction}
        />
      )}
    </div>
  );
};

export default CharacterizationRow;
